import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import isPast from 'date-fns/is_past';

import * as ModalDialogActions from 'rebook/apps/base/actions/ModalDialogActions';
import * as NotificationActions from 'rebook/apps/users/actions/NotificationActions';
import SubscriptionActiveTill from 'rebook/apps/payments/components/SubscriptionActiveTill';
import SubscriptionTypeTitle from 'rebook/apps/payments/components/SubscriptionTypeTitle';
import PaymentMethodIcon from 'rebook/apps/payments/components/PaymentMethodIcon';
import PaymentMethodName from 'rebook/apps/payments/components/PaymentMethodName';
import { TableListItem, TableListItemIcon, TableListItemTitle, TableListItemContent }
  from 'rebook/apps/base/components/TableListItem';
import FormattedDateTime from 'rebook/apps/base/components/FormattedDateTime';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import formatPaymentMethodName from 'rebook/apps/payments/utils/formatPaymentMethodName';
import ERROR_MESSAGES from 'rebook/apps/base/components/form/constants';

import styles from './style.scss';


const DeactivateAutoRebillDialogContent = () => (
  <p>
    Вы уверены, что хотите отключить автопродление? Если у вас не останется ни одного активного
    автопродления, после окончания оплаченного периода вы потеряете доступ к каталогу MyBook.
  </p>
);

const DeactivateWalletDialogContent = () => (
  <p>
    Вы уверены, что хотите отключить кошелек?
  </p>
);

const AvailableContent = ({ wallets, subscriptionType }) => {
  return (
    <div className={styles.availableContent}>
      <span className={styles.availableContentTitle}>Доступ</span>
      {(wallets && wallets.length > 0) ? wallets.map((item, i) => (
        <div key={`${item.type}-${i}`} className={styles.availableType}>
          {(subscriptionType === 'pro' || subscriptionType === 'audio') && (
            <span className={styles.availableTypeBooks}>Все книги</span>
          )}
          {subscriptionType === 'standard' && (
            <span className={styles.availableTypeBooks}>Классика и бестселлеры</span>
          )}
          {(subscriptionType === 'audio') && (
            <span className={styles.availableTypeAudioBooks}>Аудио книги</span>
          )}
        </div>
      ))
        : (
          <div className={styles.availableType}>
            {(subscriptionType === 'pro' || subscriptionType === 'audio') && (
              <span className={styles.availableTypeBooks}>Все книги</span>
            )}
            {subscriptionType === 'standard' && (
              <span className={styles.availableTypeBooks}>Классика и бестселлеры</span>
            )}
            {(subscriptionType === 'audio') && (
              <span className={styles.availableTypeAudioBooks}>Аудио книги</span>
            )}
          </div>
        )}
    </div>
  );
};

class UserSubscriptionListSubscriptionData extends React.Component {
  constructor(props) {
    super(props);
    this.walletDeactivationIsLocked = false;
  }

  componentDidCatch(error, errorInfo) {
    Raven.captureException(error, { extra: errorInfo });
  }

  toggleWalletDeactivationLock(isLocked) {
    this.walletDeactivationIsLocked = isLocked;
  }

  showDeactivateWalletDialog(wallet, isSubscriptionInPast) {
    if (this.walletDeactivationIsLocked) {
      return null;
    }

    const { ModalDialogActions } = this.props;

    ModalDialogActions.pop({
      title: `Отключение ${isSubscriptionInPast ? 'кошелька' : 'автопродления'}`,
      emojiIcon: '😢',
      extraClassName: 'jest-deactivate-rebilling-dialog',
      content: isSubscriptionInPast ? <DeactivateWalletDialogContent /> : <DeactivateAutoRebillDialogContent />,
      actions: [
        {
          id: 1,
          content: 'Да, отключить',
          extraClassName: 'link jest-deactivate-rebilling-dialog-accept',
          onClick: () => this.deactivateWallet(wallet, isSubscriptionInPast),
        },
        {
          id: 2,
          content: 'Нет, не отключать',
          extraClassName: 'jest-deactivate-rebilling-dialog-decline',
        },
      ],
    });
  }

  deactivateWallet(wallet, isSubscriptionInPast) {
    const { NotificationActions, onDeactivateWallet } = this.props;
    ga('send', 'event', 'PaymentSettings', 'Unsubscribe');
    this.toggleWalletDeactivationLock(true);

    fetchQuery(`/api/wallets/${wallet.id}/deactivate/`, { method: 'POST' })
      .then(
        () => {
          this.toggleWalletDeactivationLock(false);
          const methodName = formatPaymentMethodName(wallet.slug, 'gen');
          const message = isSubscriptionInPast
            ? 'Кошелек отключен'
            : `Автопродление для ${methodName} отключено`;
          NotificationActions.pop(message, 'done');
          if (onDeactivateWallet) {
            onDeactivateWallet(wallet.id);
          }
        }
      )
      .catch(
        ({ resp, data }) => {
          this.toggleWalletDeactivationLock(false);
          if (resp.status === 500) {
            NotificationActions.pop(ERROR_MESSAGES.SERVER_ERROR, 'error');
            Raven.captureMessage('failed to deactivate wallet due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                content: data,
              },
            });
          }
        }
      );
  }

  render() {
    const { activeTill, type, wallets } = this.props;
    const isSubscriptionInPast = isPast(activeTill);

    return (
      <div className={classNames(styles.container, 'jest-subscription-data', `jest-subscription-data-${type}`)}>
        <SubscriptionTypeTitle
          subscriptionType={type}
          extraClassName="billetContainerHeader" />
        <SubscriptionActiveTill date={activeTill} />
        {wallets && wallets.length > 0 ? (
          <div className={styles.billetContainerContent}>
            <AvailableContent wallets={wallets} subscriptionType={type} />
            {wallets.map(
              (item) => (
                <TableListItem key={item.id} extraClassName={styles.walletItem}>
                  <TableListItemIcon>
                    <PaymentMethodIcon slug={item.slug} />
                  </TableListItemIcon>
                  {item.next_rebill_date && (
                    <TableListItemTitle>
                      <div>
                        {`${isPast(item.next_rebill_date) ? 'Срок списания прошел' : 'Следующее списание'}`}{' '}
                        <FormattedDateTime timestamp={item.next_rebill_date} format="D MMMM YYYY" noWrap />
                      </div>
                    </TableListItemTitle>
                  )}
                  <TableListItemContent>
                    <PaymentMethodName slug={item.slug} description={item.safe_wallet_num} />
                  </TableListItemContent>
                  {!item.is_deactivatable
                    ? (
                      <div>
                        <small>
                          У вас есть подписка, оформленная через {formatPaymentMethodName(item.slug, 'acc')},
                          а значит, если вы захотите купить подписку на сайте, вам нужно заранее ее отключить,
                          иначе с вас спишут деньги два раза.
                        </small>
                        <div className={styles.subscriptionStatus}>
                          {item.slug === 'itunes' && (
                            <Link
                              to="/help/biblioteka-dlya-ios-mybook/kak-otmenit-podpisku-oformlennuyu-na-ustrojstve-io/"
                              className="link jest-deactivate-ios">
                              Как отключить?
                            </Link>
                          )}
                          {item.slug === 'google-play' && (
                            <Link
                              to="/help/biblioteka-dlya-android-2/kak-otmenit-prodlenie-podpiski-oformlennoj-na-an-2/"
                              className="link jest-deactivate-android">
                              Как отключить?
                            </Link>
                          )}
                        </div>
                      </div>
                    )
                    : (
                      <div className={styles.subscriptionStatus}>
                        <span
                          className={classNames('link', `jest-deactivate-rebilling-${item.id}`)}
                          onClick={() => this.showDeactivateWalletDialog(item, isSubscriptionInPast)}>
                          {`Отключить ${isSubscriptionInPast ? 'кошелек' : 'автопродление*'}`}
                        </span>
                      </div>
                    )
                  }
                </TableListItem>
              )
            )}
          </div>
        )
          : (
            <div className={styles.billetContainerContent}>
              <AvailableContent wallets={wallets} subscriptionType={type} />
            </div>
          )}
      </div>
    );
  }
}

UserSubscriptionListSubscriptionData.propTypes = {
  activeTill: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  wallets: PropTypes.arrayOf(
    PropTypes.shape({
      is_deactivatable: PropTypes.bool,
      next_rebill_date: PropTypes.string,
      safe_wallet_num: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
};

function mapStateToProps(state) {
  return {
    user: state.user,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    ModalDialogActions: bindActionCreators(ModalDialogActions, dispatch),
    NotificationActions: bindActionCreators(NotificationActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSubscriptionListSubscriptionData);
